import { useEffect } from 'react'
import create from 'zustand'
import { getBillingCatalog } from '../api/catalogApi'

// The plan catalog (GET /v1/public/billing/catalog), loaded once per page load and shared by the
// Billing tab, the sign-up plan picker, upgrade prompts and the API error messages. Not persisted:
// a plan change in the database shows up on the next page load.
let inflight = null

const useCatalogStore = create((set, get) => ({
  catalog: null,
  status: 'idle', // idle | loading | ready | error

  load: () => {
    if (get().status === 'ready') return Promise.resolve(get().catalog)
    if (inflight) return inflight
    set({ status: 'loading' })
    inflight = getBillingCatalog()
      .then((catalog) => {
        set({ catalog, status: 'ready' })
        return catalog
      })
      .catch((error) => {
        set({ status: 'error' })
        throw error
      })
      .finally(() => {
        inflight = null
      })
    return inflight
  },

  reload: () => {
    set({ status: 'idle' })
    return get().load()
  },

  reset: () => {
    inflight = null
    set({ catalog: null, status: 'idle' })
  }
}))

/** Hook: the catalog, loading it on first use. `status` is idle | loading | ready | error. */
export const useCatalog = () => {
  const catalog = useCatalogStore((s) => s.catalog)
  const status = useCatalogStore((s) => s.status)
  const load = useCatalogStore((s) => s.load)
  const reload = useCatalogStore((s) => s.reload)
  useEffect(() => {
    if (status === 'idle') load().catch(() => {})
  }, [status, load])
  return { catalog, status, reload }
}

export { useCatalogStore }
export default useCatalogStore
